import Dexie from "dexie";
import localforage from "localforage";
import { appId, initForage, tableConfigs } from "./Table_Configs";
import { parseXml, pFetch } from "./helpers";
const forageDataString = `${appId}_tables`;
export const forageAppDataString = `${appId}_metadata`;
const all = Dexie.Promise.all;
export const db = new Dexie(appId);
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const getLastSyncCheck = () =>
  localforage.getItem(forageAppDataString).then((data) =>
    data?.lastSyncCheck ?? 100
  );
export function withExtras(tableName, extras = []) {
  return async (recordsArray) => {
    let records = recordsArray;
    if (records) {
      for (let i = 0; i < (extras.length || 0); i++) {
        const extrasFunction = extras[i];
        records = await extrasFunction(tableName)(records);
      }
    }
    return records;
  };
}
const getParents = (records, relationshipField) => {
  const foreignKeys = records.map((record) =>
    record[relationshipField.label] || 0
  );
  return db[relationshipField.relTo].bulkGet(foreignKeys);
};

export function withParents(tableName) {
  return async (recordsArray) => {
    const { fields } = tableConfigs[tableName];
    const relationShipFields = fields.filter((field) => field.relTo);
    const relationshipRecords = await all(
      relationShipFields.map((relationshipField) =>
        getParents(recordsArray, relationshipField)
      ),
    );

    for (let i = 0; i < relationShipFields.length; i++) {
      const relationShipField = relationShipFields[i];
      const relatedRecords = relationshipRecords[i];
      recordsArray.forEach((record, idx) =>
        Object.defineProperty(record, relationShipField.label + "_record", {
          value: relatedRecords[idx],
          enumerable: false,
          configurable: true,
          writable: true,
        })
      );
    }

    return recordsArray;
  };
}

export const withFormulas = (tableName) => {
  return (recordsArray) => {
    const { fields } = tableConfigs[tableName];
    const formulaFields = fields.filter((field) => field.formulaFunction);
    return all(recordsArray.map(async (record) => {
      await record;
      return formulaFields.reduce((obj, field) => {
        const cachedValue = obj[field.label];
        return Object.defineProperties(obj, {
          [field.label]: {
            get: () => field.formulaFunction(obj),
            configurable: true,
          },
          [field.label + "_lastCachedValue"]: {
            value: cachedValue,
            enumerable: false,
            configurable: true,
            writable: true,
          },
        });
      }, record);
    }));
  };
};
export const reserveNextTempRecordId = (tableName) => {
  let id = Number(
    window.localStorage.getItem(`${appId}_${tableName}_MinTempId`) || 0,
  );
  ++id;
  window.localStorage.setItem(`${appId}_${tableName}_MinTempId`, String(id));
  return -((Date.now() - 1727100000000) + Math.random());
};

const getFieldsToFetch = (fields) =>
  fields.map((field) => field.id).filter((id) => id > 0);

const getPostingFields = (fields) =>
  fields.filter((field) => field.editable && field.id > 0);
const getKey = (tableName) =>
  tableConfigs[tableName].fields.find((field) => field.type === "key");
const getKeyField = (fields) => {
  const keyField = fields.find((field) => field.type === "key");
  return [keyField.label, keyField.id];
};

const getPostingBody = (tableName, records) => {
  const { tableId, fields } = tableConfigs[tableName];
  const fieldsToPost = getPostingFields(fields);
  const fieldsToReturn = getFieldsToFetch(fields);
  const [keyFieldLabel, keyFieldId] = getKeyField(fields);
  const tempIdField = fields.find((field) => field.label === "tempId");

  const data = [];

  records.forEach((record) => {
    const obj = {};
    if (record[keyFieldLabel] > 0) {
      obj[keyFieldId] = { value: record[keyFieldLabel] };
    }
    fieldsToPost.forEach((field) => {
      if (record[field.label] !== undefined) {
        const value = field.sendAs
          ? field.sendAs(record[field.label])
          : record[field.label];
        obj[field.id] = { value: value };
      }
    });
    data.push(obj);
  });

  const body = {
    to: tableId,
    mergeFieldId: tempIdField.id,
    data,
    fieldsToReturn,
  };
  return JSON.stringify(body);
};

const tempRecordsNum = 0;

export async function createLocalRecord(tableName, object = {}) {
  const { fields } = tableConfigs[tableName];
  const keyField = fields.find((field) => field.type === "key").label;
  const tempId = await reserveNextTempRecordId(tableName);
  object[keyField] = tempId;
  object.tempId = tempId;
  object.locallyUpdatedAt = Date.now();

  return db[tableName].add(object);
}
function createTableSchema(tableName, fields) {
  const schema = fields.map((field) => {
    if (field.type === "key") {
      return "";
    } else {
      return field.label; // Use label as column name
    }
  });

  // Find the field with type 'recordid'
  const recordIdField = fields.find((field) => field.type === "key").label;
  const primaryKey = recordIdField ? recordIdField : "id"; // Default primary key
  const tables = {};
  tables[tableName] = `${primaryKey},` + schema.filter(Boolean).join(",");
  try {
    db.version(9).stores(tables);
  } catch (error) {
    console.error(error);
  }
}

function initializeAllTables() {
  // Initialize table schemas
  db.close();
  Object.keys(tableConfigs).forEach((tableName) => {
    const { fields } = tableConfigs[tableName];
    createTableSchema(tableName, fields);
  });
  db.open();
  initForage(db);
}
initializeAllTables();

export function initializeAndFetchAllTables(
  hostName,
  userAgent,
  quickbaseFetch,
) {
  const getTableUpdates = async (retry = 5) => {
    if (retry <= 0) {
      throw new Error(
        "Retried getTableUpdates too many times without a succesfull fetch",
      );
    }
    const url =
      `https://atlas-titan.quickbase.com/db/main?a=API_GetAppDTMInfo&dbid=${appId}`;
    try {
      const response = await pFetch(url, {
        method: "GET",
        headers: {
          "X_QUICKBASE_RETURN_HTTP_ERROR": "true",
          "Content-Type": "application/xml",
        },
      });

      if (response.ok) {
        const data = await response.text();
        const parsed = parseXml(data);
        const tables = parsed.qdbapi.tables.table;
        const tableModifiedObj = {};
        tables.forEach((table) => {
          tableModifiedObj[table.id] = Number(table.lastRecModTime["#text"]);
        });
        const finalObject = {};
        Object.keys(tableConfigs).forEach((tableName) => {
          const table = tableConfigs[tableName];

          finalObject[tableName] = tableModifiedObj[table.tableId];
        });

        return finalObject;
      }
    } catch (error) {
      console.error(error);
      if (error.body) {
        await delay(4000);
        return getTableUpdates(retry - 1);
      } else {
        //throw new Error(`Received ERROR while getting table updates ${error}`);
      }
    }
  };
  // Initialize Dexie database
  const postAndUpdateDexie = async (tableName, records) => {
    const body = getPostingBody(tableName, records);
    const apiUrl = `https://api.quickbase.com/v1/records`;
    try {
      const response = await quickbaseFetch(apiUrl, {
        method: "POST",
        headers: {
          "QB-Realm-Hostname": hostName,
          "User-Agent": userAgent,
          "Content-Type": "application/json",
        },
        body,
      });

      if (response.status >= 200 && response.status < 300) {
        const { body } = response;
        const success = await storeTableDataFromFetch(tableName, body, false);
        return success;
      } else {
        console.error(
          `Failed to fetch data for table '${tableName}':`,
          response.statusText,
        );
        return false;
      }
    } catch (error) {
      console.error(`Error fetching data for table '${tableName}':`, error);
    }
  };

  async function fetchAndStoreTableData(tableName) {
    const tableConfig = tableConfigs[tableName];

    if (!tableConfig) {
      console.error(`Table configuration for '${tableName}' not found.`);
      return;
    }

    const { tableId, fields, fetchFilter } = tableConfig;
    const selectedFields = getFieldsToFetch(fields);
    const apiUrl = "https://api.quickbase.com/v1/records/query";
    const syncedAt = Date.now();
    try {
      const response = await quickbaseFetch(apiUrl, {
        method: "POST",
        headers: {
          "QB-Realm-Hostname": hostName,
          "User-Agent": userAgent,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          "from": tableId,
          "select": selectedFields,
          ...(fetchFilter ? { "where": fetchFilter } : {}),
        }),
      });

      if (response.status >= 200 && response.status < 300) {
        const { body } = response;
        await storeTableDataFromFetch(tableName, body);
        const forageData = await localforage.getItem(forageDataString);
        forageData[tableName].tableLastSynced = syncedAt;
        await localforage.setItem(forageDataString, forageData);
      } else {
        console.error(
          `Failed to fetch data for table '${tableName}':`,
          response.statusText,
        );
      }
    } catch (error) {
      console.error(`Error fetching data for table '${tableName}':`, error);
    }
  }

  async function fetchAndStoreAllTables() {
    const appData = await localforage.getItem(forageAppDataString);
    await localforage.setItem(forageAppDataString, {
      ...appData,
      lastSyncCheck: Date.now(),
    });

    return Promise.all(
      Object.keys(tableConfigs).map((tableName) =>
        fetchAndStoreTableData(tableName)
      ),
    );
  }

  async function checkAndSyncTables() {
    const tableUpdates = await getTableUpdates();
    const forageData = await localforage.getItem(forageDataString);
    if (!(tableUpdates && forageData)) {
      console.error("Couldn't get neccessary info to sync");
      return false;
    }
    const appData = await localforage.getItem(forageAppDataString);

    const tablesToUpdate = Object.keys(tableUpdates).filter((tableName) => {
      const quickBaseModified = tableUpdates[tableName];
      const localUpdated = forageData[tableName].tableLastSynced;

      return quickBaseModified > localUpdated;
    });
    await localforage.setItem(forageAppDataString, {
      ...appData,
      lastSyncCheck: Date.now(),
    });
    return Promise.all(tablesToUpdate.map((tableName) => {
      return fetchAndStoreTableData(tableName);
    }));
  }

  async function syncIfTimePassed(timeout = 30000) {
    const now = Date.now();
    const { lastSyncCheck } = await localforage.getItem(forageAppDataString);
    const timeSinceLastSync = now - lastSyncCheck;
    if (now - lastSyncCheck > timeout) {
      return checkAndSyncTables();
    } else {
      console.warn("Timeout was not reached", { timeSinceLastSync, timeout });
    }
  }

  async function postRecords(tableName, recordKeys = [], recurse = 0) {
    const filterRecords = (record) => {
      if (!record) {
        console.warn(`filtered out post for bad record ${tableName}`, record);
        return false;
      }

      // For line item tables, check if it needs to be posted to Quickbase
      if (
        tableName === "laborLineItems" || tableName === "equipmentLineItems"
      ) {
        if (
          record[keyField] < 0 || !record.postedToQuickbase ||
          ((record.dateModified && record.locallyUpdatedAt) &&
            (new Date(record.dateModified).getTime() < record.locallyUpdatedAt))
        ) {
          return true;
        } else {
          console.warn(
            `filtered out post for already posted line item ${tableName}`,
            record,
          );
          return false;
        }
      } else { // For other tables (like dailyFieldReports)
        if (
          record[keyField] < 0 ||
          ((record.dateModified && record.locallyUpdatedAt) &&
            (new Date(record.dateModified).getTime() < record.locallyUpdatedAt))
        ) {
          return true;
        } else {
          return false;
        }
      }
    };
    const { fields, childTables } = tableConfigs[tableName];
    const keyField = fields.find((field) => field.type === "key").label;
    let records = await db[tableName].bulkGet(recordKeys).then((records) =>
      withExtras(tableName, [
        withParents,
        withFormulas,
        () => (recordsArray) => recordsArray.filter(filterRecords),
      ])(records)
    );

    if (records.length === 0) {
      return false;
    }
    if (records.every((record) => !record)) {
      console.error(
        `No ${tableName} found with the supplied keys: ${recordKeys}`,
      );
      return false;
    }
    const parentFields = fields.filter((field) => field.relTo);

    const relUpdates = {};

    parentFields.forEach((field) => {
      records.forEach((record) => {
        if (
          typeof record[field.label] === "number" &&
          record[field.label] > tempRecordsNum
        ) {
          relUpdates[field.relTo]
            ? relUpdates[field.relTo].push(record[field.label])
            : relUpdates[field.relTo] = [record[field.label]];
        }
      });
    });

    const postParentsPromises = Object.keys(relUpdates).map((parentTableName) =>
      postRecords(parentTableName, relUpdates[parentTableName])
    );

    await Promise.all(postParentsPromises);
    records = await db[tableName].bulkGet(recordKeys).then((records) =>
      withExtras(tableName, [
        withParents,
        withFormulas,
        () => (recordsArray) => recordsArray.filter(filterRecords),
      ])(records)
    ); //Parents should be posted and have updated all related children in the dexieDB
    const success = await postAndUpdateDexie(tableName, records);
    if (!success) {
      console.error(
        `Call to postandUpdateDexie(${tableName}, {records}) failed.`,
        { records },
      );
      return false;
    }
    if (recurse > 0) {
      const newIds = await db[tableName].where("tempId").anyOf(recordKeys)
        .primaryKeys();

      await Promise.all(
        Object.keys(childTables).map(async (childTableName) => {
          const childRelField = childTables[childTableName].label;

          const childKeys = await db[childTableName].where(childRelField).anyOf(
            newIds,
          ).primaryKeys();

          return postRecords(childTableName, childKeys, recurse - 1);
        }),
      );
    } else {
      return true;
    }
  }

  async function storeTableDataFromFetch(
    tableName,
    apiResponse,
    clearData = true,
  ) {
    const { data } = apiResponse;
    const { fields, childTables } = tableConfigs[tableName]; //await db[`${tableName}_fields`].toArray();
    const keyField = fields.find((field) => field.type === "key").label;

    let formattedData = data.map((record) => {
      const recordObj = fields.reduce((obj, field) => {
        if (record[field.id]) {
          const value = field.convertTo
            ? field.convertTo(record[field.id].value)
            : record[field.id].value;
          obj[field.label] = value;
        }
        return obj;
      }, {});

      // Set postedToQuickbase to true for records coming from Quickbase (i.e., having a real recordId)
      if (recordObj[keyField] > 0) { // Assuming keyField is 'recordId' and positive means it's from QB
        recordObj.postedToQuickbase = true;
      }

      // Synchronize locallyUpdatedAt with dateModified from Quickbase
      if (record[2] && record[2].value) { // Check if dateModified (id: 2) exists in Quickbase response
        recordObj.locallyUpdatedAt = new Date(record[2].value).getTime();
      }

      return recordObj;
    });

    if (formattedData) {
      if (clearData) {
        const keysToSave = await db[tableName].filter((record) => {
          if (record[keyField] > 0 && record.locallyUpdatedAt) {
            const newRecord = formattedData.find((rec) =>
              rec[keyField] === record[keyField]
            );
            if (
              newRecord &&
              new Date(newRecord.dateModified).getTime() <
                record.locallyUpdatedAt
            ) {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        }).primaryKeys();
        try {
          await db[tableName].where("recordId").above(tempRecordsNum).and(
            (rec) => {
              return !(keysToSave.includes(rec[keyField]));
            },
          ).delete();
        } catch (error) {
          console.error(error);
        }
        formattedData = formattedData.filter((rec) =>
          !(keysToSave.includes(rec[keyField]))
        );
      }
      await db[tableName].bulkPut(formattedData);

      const tempIdField = fields.find((field) => field.label === "tempId");
      if (tempIdField) {
        const tempIdsInResponse = formattedData.map((rec) => rec.tempId).filter(
          Boolean,
        );
        if (tempIdsInResponse.length > 0) {
          await db[tableName].bulkDelete(tempIdsInResponse);
        }
      }

      const dexieRecords = formattedData; //await db[tableName].bulkGet(recordKeys);
      const locallyCreated = dexieRecords.filter((record) => record.tempId);
      const recordsNeedingChecked = locallyCreated; //.filter(record => !(record.childrenUpdated))
      const recordsNeedingCheckedIds = recordsNeedingChecked.map((record) =>
        record[keyField]
      );
      await Promise.all(recordsNeedingChecked.map((record) => {
        return Promise.all(
          Object.keys(childTables).map((childTableName) => {
            const relFieldLabel = childTables[childTableName].label;
            const updateObj = {};
            updateObj[relFieldLabel] = record[keyField];

            return db[childTableName].where(relFieldLabel).equals(record.tempId)
              .modify(updateObj);
          }),
        );
      }));
      await db[tableName].where("recordId").anyOf(recordsNeedingCheckedIds)
        .modify({ childrenUpdated: true });
      return true;
    } else {
      return false;
    }
  }

  async function reconcileLineItems() {
    if (window.localStorage.getItem(`${appId}_IsSubmitting`) !== "false") {
      return;
    }
    // Find all dailyFieldReports that have been posted to Quickbase
    const postedDfrs = await db.dailyFieldReports.where("recordId").above(0)
      .toArray();

    for (const dfr of postedDfrs) {
      const dfrId = dfr.recordId;

      // Check laborLineItems
      const pendingLaborLineItems = await db.laborLineItems
        .where("relatedDailyFieldReport").equals(dfrId)
        .and((item) => !item.postedToQuickbase)
        .toArray();

      if (pendingLaborLineItems.length > 0) {
        await postRecords(
          "laborLineItems",
          pendingLaborLineItems.map((item) => item.recordId),
        );
      }

      // Check equipmentLineItems
      const pendingEquipmentLineItems = await db.equipmentLineItems
        .where("relatedDailyFieldReport").equals(dfrId)
        .and((item) => !item.postedToQuickbase)
        .toArray();

      if (pendingEquipmentLineItems.length > 0) {
        await postRecords(
          "equipmentLineItems",
          pendingEquipmentLineItems.map((item) => item.recordId),
        );
      }
    }
  }

  async function clearData() {
    await localforage.removeItem(forageDataString);
    await Promise.all(
      Object.keys(tableConfigs).map((tableName) => db[tableName].clear()),
    );
    window.location.reload();
  }

  async function clearFetchedData() {
    const first = localforage.getItem(forageDataString).then((data) => {
      Object.keys(data).forEach((tableName) =>
        data[tableName].tableLastSynced = 100
      );
      localforage.setItem(forageDataString, data);
    });
    const second = localforage.setItem(forageAppDataString, {
      lastSyncCheck: 100,
    });
    return Promise.all([
      first,
      second,
      ...(Object.keys(tableConfigs).map((tableName) =>
        db[tableName].where(getKey(tableName).label).above(0).delete()
      )),
    ]);
  }

  // Return the functions in an object for easy access
  return {
    fetchAndStoreTableData,
    fetchAndStoreAllTables,
    storeTableDataFromFetch,
    postRecords,
    getTableUpdates,
    checkAndSyncTables,
    syncIfTimePassed,
    clearData,
    clearFetchedData,
    reconcileLineItems,
  };
}
